/**
 * SPA-root gate. Picks ONE of three top-level outcomes:
 *
 *   ┌──────────────────────────┬───────────────────────────────────────────┐
 *   │ Helper status            │ Rendered                                  │
 *   ├──────────────────────────┼───────────────────────────────────────────┤
 *   │ reachable, enabled       │ <children/>                               │
 *   │ reachable, kill-switched │ <MaintenanceScreen/>                      │
 *   │ unreachable (network) +  │ <Navigate to="/local-service" replace/>   │
 *   │   ADH-required path      │                                           │
 *   │ unreachable + ADH-free   │ <children/>  (Home / Settings / Tools)    │
 *   └──────────────────────────┴───────────────────────────────────────────┘
 *
 * Placement: mounts ABOVE <AuthGate> in main.tsx, so:
 *
 *   - Operator can flip service.enabled=false on a hub the user has never
 *     signed into; the maintenance copy still renders (the /api/config
 *     endpoint is auth-open).
 *   - When the kill switch is on but the user has a valid cookie, we
 *     STILL show maintenance (auth state is irrelevant — the whole app
 *     is off).
 *   - When the helper isn't running AND the user is on an ADH-required
 *     route (/devices/*, /static-analysis/*), we redirect to
 *     `/local-service`. That page has the "启动服务" button + status panel
 *     and IS the canonical "start adh" UX. The URL change is important:
 *     it lets sidebar 本机服务 highlight active, and the user understands
 *     "I'm now on the start-service page" rather than "I'm on a device
 *     URL with a weird overlay".
 *
 * Boot ordering & flicker-avoidance:
 *
 *   1. `useRuntimeConfig.startPolling()` is invoked from a top-level
 *      effect, firing the first fetch alongside the rest of app boot.
 *   2. Until that first fetch settles, `initialised === false`. We render
 *      children optimistically rather than a splash — 99% of users hit a
 *      healthy helper and don't deserve a flash.
 *   3. After the first failure, we only escalate (Navigate-redirect)
 *      if the failure is a network-class error. HTTP 5xx → leave the UI
 *      up and let the per-page error boundaries take over (the helper
 *      might recover on the next poll).
 *   4. We trip the redirect on the FIRST network-class failure because
 *      localhost TCP is deterministic (no flake regime). Waiting for a
 *      second confirming failure would make the user stare at a broken
 *      main UI for one whole 30s poll period.
 */
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useRuntimeConfig } from '@/lib/runtimeConfig';
import { isAdhRequiredPath } from '@/lib/adhRequiredPaths';
import { MaintenanceScreen } from './MaintenanceScreen';
import { ReloadingScreen } from './ReloadingScreen';

// On localhost, a `fetch` to 127.0.0.1 either succeeds within ~10ms or
// errors instantly with ECONNREFUSED — there is no "flaky transport"
// regime. So a single network-class failure is unambiguous: the helper
// isn't bound. Surface the offline screen immediately rather than
// making the user wait 30s for a second confirming failure.
//
// (The previous threshold of 2 was a hangover from designing for remote
// HTTP fetches, which can legitimately blip during a route change. Our
// HTTP 5xx case is already classified as 'http' and never reaches this
// branch, so we don't need belt-and-suspenders here.)
const OFFLINE_FAILURE_THRESHOLD = 1;

export function ServiceGate({ children }: { children: React.ReactNode }): JSX.Element {
  const startPolling = useRuntimeConfig((s) => s.startPolling);
  const stopPolling = useRuntimeConfig((s) => s.stopPolling);
  const enabled = useRuntimeConfig((s) => s.config.service.enabled);
  const initialised = useRuntimeConfig((s) => s.initialised);
  const lastError = useRuntimeConfig((s) => s.lastError);
  const consecutiveFailure = useRuntimeConfig((s) => s.consecutiveFailure);
  const source = useRuntimeConfig((s) => s.source);
  const pendingHelperRestartReload = useRuntimeConfig(
    (s) => s.pendingHelperRestartReload,
  );

  // path-aware gating: only ADH-REQUIRED routes are full-screen
  // hijacked when the agent is unreachable. ADH-FREE routes (tools,
  // settings, local-service, home) keep rendering so the user can
  // start the agent without losing context.
  const location = useLocation();
  const needsAdh = isAdhRequiredPath(location.pathname);

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  // Top priority: we detected a helper restart and are seconds away
  // from `location.reload()`. Render the transition card so the user
  // doesn't see a one-frame flash of the main UI between cache reset
  // and the actual nav. See web/src/lib/runtimeConfig.ts and
  // web/src/components/ReloadingScreen.tsx for the full chain.
  if (pendingHelperRestartReload) {
    return <ReloadingScreen />;
  }

  // Next priority: kill switch is engaged. Only honour this from a
  // fresh server payload (`source === 'server'`); a stale `cache` value
  // could leave us stuck on maintenance forever if the user once visited
  // a kill-switched hub but is now connecting to a healthy one.
  //
  // The kill switch is a hub-wide "service disabled" flag and must
  // apply EVERYWHERE (including ADH-FREE routes) — it's how ops
  // explicitly black out the app.
  if (initialised && source === 'server' && !enabled) {
    return <MaintenanceScreen />;
  }

  // Helper offline. Only intercepts when the user is on an
  // ADH-REQUIRED route — otherwise we let them keep using offline-safe
  // pages and rely on Home / Sidebar to surface the agent status.
  //
  // 改为 redirect 到 `/settings#device`：「手机设备设置」tab 本来就是
  // 启停 adh 进程的入口，有 "启动服务" 按钮 + 完整运行状态。URL 一起改，
  // sidebar 的 "设置" 项高亮成 active，用户立刻明白 "现在需要先去启动 adh"。
  if (
    initialised &&
    lastError?.kind === 'network' &&
    consecutiveFailure >= OFFLINE_FAILURE_THRESHOLD &&
    needsAdh
  ) {
    // `state.autoLaunch=true` 是给 DeviceSettingsTab 的 hint：
    // 用户**被迫**来到这里（点了 /devices/:serial 但 adh 没在跑），
    // 不是主动来停服务的，自动 fire `adh://launch` 省一次点击。
    return (
      <Navigate
        to="/settings#device"
        replace
        state={{ autoLaunch: true, from: location.pathname }}
      />
    );
  }

  return <>{children}</>;
}
